import { Injectable, inject } from '@angular/core';
import { fromJson, toJson } from '@bufbuild/protobuf';
import {
  SettingsServiceLoadResponseJson,
  SettingsServiceLoadResponseSchema,
  SettingsServicePersistDropboxRequestJson,
  SettingsServicePersistDropboxRequestSchema,
  SettingsServicePersistProjectPermitRequestJson,
  SettingsServicePersistProjectPermitRequestSchema,
  SettingsServicePersistProjectSurveyRequestJson,
  SettingsServicePersistProjectSurveyRequestSchema,
} from '@vmspdb/definitions';
import { ConnectSettings } from '@vmspdb/ui/connect';
import { Observable, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SettingsRepositoryService {
  private client = inject(ConnectSettings);

  load(): Observable<SettingsServiceLoadResponseJson> {
    return this.client['load']({}).pipe(
      map(
        (result) =>
          toJson(
            SettingsServiceLoadResponseSchema,
            result,
          ) as SettingsServiceLoadResponseJson,
      ),
    );
  }

  // These three requests carry `updatedAt`/`updater` fields straight through
  // from store state (which is consistently *Json-typed, per this repo's
  // enum/timestamp conventions) - e.g. `updatedAt` as an RFC3339 string. A
  // bare `as unknown as any` hands that plain JSON object to the
  // ObservableClient as if it were a MessageInitShape, which `create()`
  // (not `fromJson()`) uses to build the outgoing message: `create()` does
  // NOT parse a Timestamp field from an RFC3339 string the way `fromJson()`
  // does, so the string was stored verbatim and only blew up later, deep in
  // connect-web's own request serialization, as an opaque
  // `RangeError: Invalid time value` (protobuf-es's `timestampToJson`
  // calling `.toISOString()` on a field it expected to already be
  // `{seconds, nanos}`) - silently dropping every submit with no HTTP
  // request ever sent. Routing through `fromJson()` here (matching `load()`
  // above, and this repo's established *Json-in/out convention) converts
  // the RFC3339 string correctly before the message is built.
  updateDropbox(dropbox: SettingsServicePersistDropboxRequestJson) {
    return this.client.persistDropbox(
      fromJson(SettingsServicePersistDropboxRequestSchema, dropbox),
    );
  }

  updateProjectPermit(
    settings: SettingsServicePersistProjectPermitRequestJson,
  ) {
    return this.client.persistProjectPermit(
      fromJson(SettingsServicePersistProjectPermitRequestSchema, settings),
    );
  }

  updateProjectSurvey(
    settings: SettingsServicePersistProjectSurveyRequestJson,
  ) {
    return this.client.persistProjectSurvey(
      fromJson(SettingsServicePersistProjectSurveyRequestSchema, settings),
    );
  }
}
